import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FirebaseError } from 'firebase/app';
import { KeyRound, Mail, User, Eye, EyeOff, Check, X } from 'lucide-react';
import { useAuth } from '../../src/context/AuthContext';
import { getStarterCheckoutUrl } from '../../src/lib/stripeBilling';
import { cn } from '../../src/lib/utils';

const META = {
  title: 'KodaCare for Pet Sitters | First 30 Free Accounts',
  description:
    'KodaCare Pet Sitters Program: free accounts for the first 30 subscribers, including up to 30 pet profiles per account.',
};

interface PasswordRule {
  label: string;
  test: (value: string) => boolean;
}

const PASSWORD_RULES: PasswordRule[] = [
  { label: 'At least 8 characters', test: v => v.length >= 8 },
  { label: 'One uppercase letter', test: v => /[A-Z]/.test(v) },
  { label: 'One lowercase letter', test: v => /[a-z]/.test(v) },
  { label: 'One number', test: v => /\d/.test(v) },
];

const APP_HOST = import.meta.env.VITE_APP_HOST || 'https://app.kodacare.pet';
const VALID_INVITE_CODE = 'SITTERFIRST30';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function mapFirebaseError(code: string): string {
  switch (code) {
    case 'auth/email-already-in-use':
      return 'This email is already registered. Use Login to continue.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/weak-password':
      return 'Your password is too weak. Please meet all password rules.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';
    case 'functions/resource-exhausted':
      return 'This offer is now fully claimed. The first 30 seats have already been allocated.';
    case 'functions/permission-denied':
      return 'Invalid invite code.';
    case 'functions/unauthenticated':
      return 'Please sign in and try again.';
    default:
      return 'Something went wrong while creating your account. Please try again.';
  }
}

export function PetSitters() {
  const { register, isAuthenticated, user, authLoading } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [inviteCodePulse, setInviteCodePulse] = useState(false);
  const inviteCodeInputRef = useRef<HTMLInputElement | null>(null);

  const passedRules = useMemo(() => PASSWORD_RULES.filter(rule => rule.test(password)).length, [password]);
  const isValidCode = inviteCode.trim().toLowerCase() === VALID_INVITE_CODE.toLowerCase();
  const hasEmailInput = email.trim().length > 0;
  const isValidEmail = EMAIL_REGEX.test(email.trim());
  const showEmailError = emailTouched && hasEmailInput && !isValidEmail;

  useEffect(() => {
    const prevTitle = document.title;
    document.title = META.title;

    const setMeta = (selector: string, attr: string, content: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, selector.match(/"([^"]+)"/)?.[1] ?? '');
        document.head.appendChild(el);
      }
      el.content = content;
    };

    setMeta('meta[name="description"]', 'name', META.description);
    setMeta('meta[property="og:title"]', 'property', META.title);
    setMeta('meta[property="og:description"]', 'property', META.description);
    setMeta('meta[property="og:type"]', 'property', 'website');
    setMeta('meta[property="og:url"]', 'property', 'https://kodacare.pet/petsitters');
    setMeta('meta[property="og:image"]', 'property', 'https://kodacare.pet/icons/og-image.png');
    setMeta('meta[name="twitter:card"]', 'name', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'name', META.title);
    setMeta('meta[name="twitter:description"]', 'name', META.description);
    setMeta('meta[name="twitter:image"]', 'name', 'https://kodacare.pet/icons/og-image.png');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    const prevCanonical = canonical.href;
    canonical.href = 'https://kodacare.pet/petsitters';

    return () => {
      document.title = prevTitle;
      if (canonical) canonical.href = prevCanonical;
    };
  }, []);

  useEffect(() => {
    const handleApplyCode = () => {
      openSignupForm(VALID_INVITE_CODE);
    };

    window.addEventListener('kc-sitter-apply-code', handleApplyCode as EventListener);
    return () => window.removeEventListener('kc-sitter-apply-code', handleApplyCode as EventListener);
  }, []);

  const openSignupForm = (code: string = VALID_INVITE_CODE) => {
    setInviteCode(code);
    setError('');
    const target = document.getElementById('sitter-signup');
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    window.setTimeout(() => {
      inviteCodeInputRef.current?.focus();
      inviteCodeInputRef.current?.select();
      setInviteCodePulse(true);
      window.setTimeout(() => setInviteCodePulse(false), 1100);
    }, 420);
  };

  const handleActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isValidCode) {
      setError('Enter your invite code to activate access.');
      return;
    }

    if (!isValidEmail) {
      setError('Please enter a valid email address.');
      setEmailTouched(true);
      return;
    }

    if (passedRules !== PASSWORD_RULES.length) {
      setError('Please meet all password requirements.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    let redirecting = false;
    try {
      const toCheckout = (uid: string, userEmail: string) => {
        const url = getStarterCheckoutUrl({
          uid,
          email: userEmail,
          name: fullName,
          campaign: 'petsitters-first30',
        });
        if (!url) throw new Error('Checkout URL could not be generated. Please try again.');
        window.location.href = url;
      };

      if (isAuthenticated && user?.id) {
        toCheckout(user.id, user.email as string);
        redirecting = true;
        return;
      }

      const uid = await register(email, password, fullName);
      toCheckout(uid, email);
      redirecting = true;
    } catch (err) {
      if (err instanceof FirebaseError) {
        setError(mapFirebaseError(err.code));
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Unable to activate right now. Please try again.');
      }
    } finally {
      if (!redirecting) setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#faf8f5] text-[#0d2744]">
      {isSubmitting && (
        <div className="fixed inset-0 z-[120] bg-[#0d2744]/35 backdrop-blur-[2px] flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 rounded-2xl bg-white/90 px-6 py-5 shadow-lg">
            <div className="h-9 w-9 rounded-full border-4 border-primary-brown/25 border-t-primary-brown animate-spin" />
            <p className="text-xs font-bold tracking-widest uppercase text-primary-brown">Activating your account…</p>
          </div>
        </div>
      )}

      <section className="pt-20 lg:pt-24 pb-6 px-6 lg:px-10 min-h-[calc(100vh-4rem)] flex items-center">
        <div className="max-w-7xl mx-auto">
          <h1
            className="text-center text-3xl sm:text-5xl lg:text-6xl font-bold leading-[0.95] tracking-tight uppercase mb-6 lg:mb-8"
            style={{
              fontFamily: '"Grozen Medical", "Atkinson Hyperlegible", ui-sans-serif, system-ui, sans-serif',
              fontWeight: 700,
            }}
          >
            Transform client anxiety
            <br />
            into clarity and confidence.
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 items-center">
            <ul className="list-disc pl-7 space-y-2.5 lg:space-y-3 text-title-primary text-base lg:text-lg leading-relaxed lg:leading-[1.45]">
              <li>Log and manage meds, vitals, diary entries, food and water intake.</li>
              <li>Never miss a dose. Track administration and share clean verified logs with owners and clinics.</li>
              <li>Free up your pet profile slots by passing ownership to your clients. You’ll keep full co-carer access and they’ll still get live updates.</li>
            </ul>

            <div className="relative pt-4 md:pt-0">
              <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-primary-brown/70 mb-2 ml-2">Coupon</p>

              <div className="relative overflow-hidden rounded-[2rem] bg-white shadow-[0_10px_24px_rgba(13,39,68,0.10)]">
                <div className="absolute left-0 top-0 h-full w-24 bg-primary-brown flex items-center justify-center">
                  <span className="[writing-mode:vertical-rl] rotate-180 text-white text-lg sm:text-2xl font-black tracking-[0.14em] uppercase">
                    Discount
                  </span>
                </div>

                <span className="absolute left-[5.25rem] top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-[#faf8f5]" aria-hidden="true" />

                <div className="pl-32 pr-6 py-6 sm:py-8">
                  <div className="absolute right-5 top-4 w-11 h-11 rounded-full border border-primary-brown/20 bg-[#faf8f5] flex items-center justify-center">
                    <span className="w-3.5 h-3.5 rounded-full bg-[#9d1f07] mr-0.5" />
                    <span className="w-3.5 h-3.5 rounded-full bg-[#f08a2a] -ml-1.5 mt-2" />
                  </div>

                  <p className="text-2xl sm:text-3xl font-bold text-title-primary/75">100% off for life!</p>
                  <p className="text-5xl sm:text-7xl font-black text-title-primary leading-none mt-1 mb-5">FIRST30</p>

                  <button
                    type="button"
                    onClick={() => openSignupForm()}
                    className="w-full rounded-full bg-primary-brown text-white text-lg font-bold py-3.5 hover:bg-primary-brown/85 transition-colors"
                  >
                    Apply Code
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-10 px-6 lg:px-10" id="sitter-signup">
        <div className="max-w-6xl mx-auto rounded-3xl p-8 lg:p-10">
          <div className="flex items-center justify-between gap-4 mb-6">
            <h2 className="text-2xl lg:text-3xl font-black uppercase">Partner Beta Program</h2>
            <a
              href={`${APP_HOST}/login`}
              className="text-sm font-semibold text-primary-brown hover:text-primary-brown/80 transition-colors"
            >
              Already have an account?
            </a>
          </div>

          {error && (
            <div className="mb-5 rounded-xl border border-error-red/20 bg-error-red/5 px-4 py-3 text-sm text-error-red">
              {error}
            </div>
          )}

          <form onSubmit={handleActivate} className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5">
            <label className="rounded-xl border border-slate-200 bg-background-light px-3 py-3 flex items-center gap-2">
              <User size={16} className="text-primary-brown/70" />
              <input
                type="text"
                required
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                className="w-full bg-transparent outline-none text-sm font-medium"
                placeholder="Your full name"
              />
            </label>

            <label className={cn(
              'rounded-xl border bg-background-light px-3 py-3 flex items-center gap-2',
              showEmailError ? 'border-error-red/40 ring-2 ring-error-red/10' : 'border-slate-200',
            )}>
              <Mail size={16} className="text-primary-brown/70" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError('');
                }}
                onBlur={() => setEmailTouched(true)}
                aria-invalid={showEmailError}
                className="w-full bg-transparent outline-none text-sm font-medium"
                placeholder="Business email"
              />
            </label>

            <label className={cn(
              'rounded-xl border px-3 py-3 flex items-center gap-2 transition-all duration-300',
              isValidCode ? 'border-positive-green/40 bg-positive-green/10' : 'border-slate-200 bg-background-light',
              inviteCodePulse && 'border-slate-200 ring-2 ring-highlight-dark/40 animate-pulse',
            )}>
              <KeyRound size={16} className={cn(isValidCode ? 'text-positive-green' : 'text-primary-brown/70')} />
              <input
                ref={inviteCodeInputRef}
                type="text"
                required
                value={inviteCode}
                onChange={e => setInviteCode(e.target.value.toUpperCase())}
                className="w-full bg-transparent outline-none text-sm font-medium tracking-[0.08em] uppercase"
                placeholder="Invite code"
                maxLength={24}
              />
            </label>

            <label className="lg:col-span-1 rounded-xl border border-slate-200 bg-background-light px-3 py-3 flex items-center gap-2">
              <KeyRound size={16} className="text-primary-brown/70" />
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full bg-transparent outline-none text-sm font-medium"
                placeholder="Password"
              />
              <button type="button" onClick={() => setShowPassword(v => !v)} className="text-gray-400 hover:text-gray-600">
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </label>

            <label className="lg:col-span-1 rounded-xl border border-slate-200 bg-background-light px-3 py-3 flex items-center gap-2">
              <KeyRound size={16} className="text-primary-brown/70" />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                required
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="w-full bg-transparent outline-none text-sm font-medium"
                placeholder="Confirm password"
              />
              <button type="button" onClick={() => setShowConfirmPassword(v => !v)} className="text-gray-400 hover:text-gray-600">
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </label>

            {(() => {
              const hasInput = inviteCode.trim().length > 0;
              const isInvalid = hasInput && !isValidCode;
              return (
                <div className={cn(
                  'lg:col-span-1 rounded-xl border px-4 py-3 flex items-center justify-between gap-3',
                  isValidCode
                    ? 'border-positive-green/30 bg-positive-green/5'
                    : isInvalid
                      ? 'border-red-300/60 bg-red-50'
                      : 'border-primary-brown/15 bg-primary-brown/5',
                )}>
                  <div>
                    <p className={cn(
                      'text-[11px] font-bold tracking-widest uppercase',
                      isValidCode ? 'text-positive-green' : isInvalid ? 'text-red-500' : 'text-primary-brown',
                    )}>System validation</p>
                    <p className="text-xs text-title-primary">
                      {isValidCode ? 'Free access code confirmed' : isInvalid ? 'Code not recognised' : 'Enter SITTERFIRST30'}
                    </p>
                  </div>
                  <span className={cn(
                    'inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-black tracking-widest uppercase',
                    isValidCode
                      ? 'bg-positive-green/20 text-positive-green'
                      : isInvalid
                        ? 'bg-red-100 text-red-600'
                        : 'bg-gray-200 text-gray-600',
                  )}>
                    {isValidCode ? 'Granted' : isInvalid ? 'Not Valid' : 'Pending'}
                  </span>
                </div>
              );
            })()}

            <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
              <ul className="space-y-1.5 text-xs">
                {PASSWORD_RULES.map(rule => {
                  const pass = rule.test(password);
                  return (
                    <li key={rule.label} className={cn('flex items-center gap-2', pass ? 'text-positive-green' : 'text-title-primary')}>
                      {pass ? <Check size={14} /> : <X size={14} />}
                      <span>{rule.label}</span>
                    </li>
                  );
                })}
              </ul>

              <button
                type="submit"
                disabled={isSubmitting || authLoading}
                className="w-full md:w-auto md:justify-self-end rounded-xl bg-primary-brown text-white px-6 py-3 text-sm font-bold hover:bg-primary-brown/85 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? 'Activating…' : 'Activate now'}
              </button>
            </div>
          </form>
        </div>
      </section>

      <section className="pb-16 px-6 lg:px-10">
        <div className="max-w-6xl mx-auto rounded-3xl p-8 lg:p-10">
          <h2 className="text-2xl font-black uppercase mb-4">A note from Phil</h2>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <img src="/me_profile.png" alt="Phil" className="w-50 h-50 rounded-full object-cover" />
            <p className="text-sm lg:text-base text-title-primary leading-relaxed max-w-3xl">
              Our mission is to empower dedicated sitters with elite tools while giving families an unbroken, lifelong record of care. Your feedback shapes this program as we build and refine KodaCare for professionals and for the community.
              <br />
              — Phil W, Co-Founder
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
